import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import apiClient, { formatApiError } from "../api";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import MediaPicker from "../components/MediaPicker";
import { ChevronLeft, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";

const StoryEditorPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === "new";
  const [form, setForm] = useState({
    title: "",
    slug: "",
    summary: "",
    body: "",
    author: "",
    hero_image_id: null,
    seo_title: "",
    seo_description: "",
    status: "draft",
  });
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(isNew);

  useEffect(() => {
    if (isNew) return;
    apiClient
      .get(`/admin/stories/${id}`)
      .then(({ data }) => {
        setForm(data);
        setLoaded(true);
      })
      .catch((e) => {
        toast.error(formatApiError(e));
        navigate("/admin/stories");
      });
  }, [id, isNew, navigate]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Auto-generate slug from title for new stories
  useEffect(() => {
    if (!isNew) return;
    if (!form.title) return;
    const slug = form.title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
    setForm((f) => ({ ...f, slug: f.slug && f.slug !== "" ? f.slug : slug }));
    // eslint-disable-next-line
  }, [form.title]);

  const onSave = async () => {
    if (!form.title || !form.slug) {
      toast.error("Titel en slug zijn verplicht.");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      if (isNew) {
        const { data } = await apiClient.post("/admin/stories", payload);
        toast.success("Verhaal aangemaakt");
        navigate(`/admin/stories/${data.id}`, { replace: true });
      } else {
        const { data } = await apiClient.patch(`/admin/stories/${id}`, payload);
        setForm(data);
        toast.success("Verhaal opgeslagen");
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async () => {
    if (!window.confirm("Verhaal definitief verwijderen?")) return;
    try {
      await apiClient.delete(`/admin/stories/${id}`);
      toast.success("Verhaal verwijderd");
      navigate("/admin/stories");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (!loaded) return <div className="text-sm text-zinc-500">Laden...</div>;

  return (
    <div data-testid="admin-story-editor" className="pb-20">
      <Link
        to="/admin/stories"
        className="inline-flex items-center gap-2 text-[11px] tracking-[0.3em] uppercase text-zinc-500 hover:text-champagne transition-colors font-body mb-8"
      >
        <ChevronLeft size={13} /> Terug naar verhalen
      </Link>

      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
        <div>
          <p className="text-[11px] tracking-[0.3em] uppercase text-champagne font-body mb-3">
            {isNew ? "Nieuw verhaal" : "Verhaal bewerken"}
          </p>
          <h1 className="font-display text-5xl text-white tracking-tight">
            {form.title || "Naamloos verhaal"}
          </h1>
        </div>
        <div className="flex items-center gap-3">
          {!isNew && (
            <button
              type="button"
              onClick={onDelete}
              data-testid="story-delete"
              className="inline-flex items-center gap-2 px-4 py-3 border border-white/10 hover:border-red-500/40 hover:text-red-400 text-zinc-300 transition-colors text-[11px] tracking-[0.3em] uppercase font-body"
            >
              <Trash2 size={12} /> Verwijderen
            </button>
          )}
          <button
            type="button"
            onClick={onSave}
            disabled={saving}
            data-testid="story-save"
            className="inline-flex items-center gap-2 px-6 py-3 bg-champagne text-ink-950 hover:bg-champagne-soft transition-colors text-[11px] tracking-[0.3em] uppercase font-body font-semibold disabled:opacity-60"
          >
            <Save size={12} /> {saving ? "Opslaan..." : "Opslaan"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-8 flex flex-col gap-6">
          <Section title="Hoofdgegevens">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Titel">
                <Input
                  data-testid="story-field-title"
                  value={form.title}
                  onChange={(e) => set("title", e.target.value)}
                  className="h-11 bg-ink-950 border-white/10 focus:border-champagne focus-visible:ring-0 text-sm font-body text-white rounded-none"
                />
              </Field>
              <Field label="Slug">
                <Input
                  data-testid="story-field-slug"
                  value={form.slug}
                  onChange={(e) => set("slug", e.target.value)}
                  className="h-11 bg-ink-950 border-white/10 focus:border-champagne focus-visible:ring-0 text-sm font-body text-white rounded-none"
                />
              </Field>
              <Field label="Auteur">
                <Input
                  value={form.author || ""}
                  onChange={(e) => set("author", e.target.value)}
                  className="h-11 bg-ink-950 border-white/10 focus:border-champagne focus-visible:ring-0 text-sm font-body text-white rounded-none"
                />
              </Field>
              <Field label="Status">
                <Select
                  value={form.status || "draft"}
                  onValueChange={(v) => set("status", v)}
                >
                  <SelectTrigger
                    data-testid="story-field-status"
                    className="h-11 bg-ink-950 border-white/10 focus:border-champagne text-sm font-body text-white rounded-none"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-ink-900 border-white/10 rounded-none">
                    <SelectItem value="draft" className="text-sm font-body rounded-none">
                      Concept
                    </SelectItem>
                    <SelectItem value="published" className="text-sm font-body rounded-none">
                      Gepubliceerd
                    </SelectItem>
                    <SelectItem value="hidden" className="text-sm font-body rounded-none">
                      Verborgen
                    </SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </div>
            <div className="mt-5">
              <MediaPicker
                label="Hoofdafbeelding"
                value={form.hero_image_id}
                onChange={(id) => set("hero_image_id", id)}
              />
            </div>
          </Section>

          <Section title="Inhoud">
            <div className="grid grid-cols-1 gap-5">
              <Field label="Korte samenvatting">
                <Textarea
                  data-testid="story-field-summary"
                  value={form.summary || ""}
                  onChange={(e) => set("summary", e.target.value)}
                  rows={3}
                  className="bg-ink-950 border-white/10 focus:border-champagne focus-visible:ring-0 text-sm font-body text-white rounded-none resize-none"
                />
              </Field>
              <Field label="Body">
                <Textarea
                  data-testid="story-field-body"
                  value={form.body || ""}
                  onChange={(e) => set("body", e.target.value)}
                  rows={14}
                  className="bg-ink-950 border-white/10 focus:border-champagne focus-visible:ring-0 text-sm font-body text-white rounded-none resize-none"
                />
              </Field>
            </div>
          </Section>

          <Section title="SEO">
            <div className="grid grid-cols-1 gap-5">
              <Field label="SEO title">
                <Input
                  value={form.seo_title || ""}
                  onChange={(e) => set("seo_title", e.target.value)}
                  className="h-11 bg-ink-950 border-white/10 focus:border-champagne focus-visible:ring-0 text-sm font-body text-white rounded-none"
                />
              </Field>
              <Field label="SEO description">
                <Textarea
                  value={form.seo_description || ""}
                  onChange={(e) => set("seo_description", e.target.value)}
                  rows={3}
                  className="bg-ink-950 border-white/10 focus:border-champagne focus-visible:ring-0 text-sm font-body text-white rounded-none resize-none"
                />
              </Field>
            </div>
          </Section>
        </div>

        <aside className="lg:col-span-4">
          <div className="bg-ink-900 border border-white/[0.06] p-6 sticky top-8">
            <p className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-body mb-4">
              Publicatie
            </p>
            <div className="text-xs text-zinc-300 font-body leading-relaxed">
              <p className="mb-3">
                <strong className="text-champagne">Concept:</strong> alleen
                zichtbaar in het CMS.
              </p>
              <p className="mb-3">
                <strong className="text-champagne">Gepubliceerd:</strong>{" "}
                zichtbaar op /verhalen en op de homepage waar van toepassing.
              </p>
              <p>
                <strong className="text-champagne">Verborgen:</strong> niet
                zichtbaar, maar wel bewaard.
              </p>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
};

const Section = ({ title, children }) => (
  <section className="bg-ink-900 border border-white/[0.06] p-7">
    <h2 className="font-display text-2xl text-white mb-6 tracking-tight">
      {title}
    </h2>
    {children}
  </section>
);

const Field = ({ label, children }) => (
  <label className="flex flex-col gap-2">
    <span className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-body">
      {label}
    </span>
    {children}
  </label>
);

export default StoryEditorPage;
